import { useEffect, useState } from "react";

import { Typography, TextField, Button, Select, DatePicker } from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/Add.svg";
import { ReactComponent as RemoveIcon } from "@king-ict/eupisi-ui/components/Icon/RemoveIcon.svg";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { DocumentUploadRequest, getUserFromOibRequest, uploadDocument } from "../../api/nisooApi";
import { ButtonWithValidation } from "../../components/ButtonWithValidation";
import StaticField from "../../components/StaticField/StaticField";
import UploadButton from "../../components/UploadButton";
import { DocumentType } from "../../models/Document";
import { TeacherLanguage, createTeacherLanguageInitial } from "../../models/ProgramRequest"
import { useGetLanguagesQuery } from "../../queries/codeBookQuery";
import styles from "./ProgramRequestEdit.module.scss";

export interface TeacherLanguageInfoProps{
    name: string;
    teacherLanguages: TeacherLanguage[];
    readonly?: boolean;
    onChange: (e: any) => void;
    getErrorForPath: (path: string) => string | undefined;
}

export default function TeacherLanguageInfo({teacherLanguages, onChange, name, getErrorForPath, readonly = false} : TeacherLanguageInfoProps):  JSX.Element{
    const { t } = useTranslation();

    const { data: languages } = useGetLanguagesQuery();
    
    useEffect(() => {
    });

    const handleLanguageChange = (event: any, index: number) => {
        const id = Number.parseInt(event.target.value);
        const language = (!isNaN(id) && languages && languages.length > 0 ? languages.find(v => v.id === id) : languages![0]) ?? {id: 1, name: ""};
        const teacherLanguageNew: TeacherLanguage = {
            ...teacherLanguages[index],
            language: language,
        };
        teacherLanguages[index] = teacherLanguageNew;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: teacherLanguages
            }
        });        
    };

    const handleDateFromChange = (event: any, index: number) => {
        const val = new Date(event.target.value);
        const teacherLanguageNew: TeacherLanguage = {
            ...teacherLanguages[index],
            dateFrom: val,
        };
        teacherLanguages[index] = teacherLanguageNew;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: teacherLanguages
            }
        });        
    };

    const handleDateToChange = (event: any, index: number) => {        
        const teacherLanguageNew: TeacherLanguage = {
            ...teacherLanguages[index],
            dateTo: event.target.value,
        };
        teacherLanguages[index] = teacherLanguageNew;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: teacherLanguages
            }
        });        
    };

    const handleCertificationDocumentAdded = async (event: any, index: number, file: File) => {          
        
        var document: DocumentData = {
            id: -1,
            fileName: file.name,
            file: file
        };

        const teacherLanguageNew: TeacherLanguage = {
            ...teacherLanguages[index],
            certificationDocument: document
        };
        teacherLanguages[index] = teacherLanguageNew;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: teacherLanguages
            }
        });
    };

    const handleCertificationDocumentRemoved = (event: any, index: number) => {
        const teacherLanguageNew: TeacherLanguage = {
            ...teacherLanguages[index],
            certificationDocument: undefined,
        };
        teacherLanguages[index] = teacherLanguageNew;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: teacherLanguages
            }
        });        
    };

    const handleAdd = (event: any) => {
        teacherLanguages.push(createTeacherLanguageInitial());
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: teacherLanguages
            }
        });
    };

    const handleRemove = (event: any, index: number) => {
        teacherLanguages.splice(index);
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: teacherLanguages
            }
        });
    };

    return (
        <>
        {teacherLanguages && teacherLanguages.length > 0 &&
            <Row>
                <Col md={12} className={styles.rowTitleSpacing}>
                    <Typography variant="smallHeading" component="h3">
                        {t("common.language")}
                    </Typography>
                </Col>
            </Row>
        }
            
            {teacherLanguages.map((teacherLanguage, index) => (
                <div className={index !== 0 ? "mt-3" : ""}>
                    <Row>
                        <Col md={3}>
                            {!readonly &&
                            <Select
                                label={t("teacherLanguage.language")}
                                name={nameof<typeof teacherLanguage>("language")}
                                value={teacherLanguage.language?.id}
                                selectData={codeBookToSelectData(languages)}
                                onChange={(e) => handleLanguageChange(e, index)}
                                errorText={getErrorForPath(`${name}${index}.language`)}
                            /> ||
                            <StaticField
                                label={t("teacherLanguage.language")}                           
                                value={teacherLanguage.language?.name}
                                className={styles.changedFieldModule}
                            />
                            }
                        </Col>
                        <Col md={3}>
                            <DatePicker
                                label={t("common.dateFrom")}
                                name={nameof<typeof teacherLanguage>("dateFrom")}
                                value={teacherLanguage.dateFrom}
                                onChange={(e) => handleDateFromChange(e, index)}
                                plaintext={readonly}
                                errorText={getErrorForPath(`${name}${index}.dateFrom`)}
                                minYear={new Date().getFullYear() - 25}
                                maxYear={new Date().getFullYear() + 25}
                            />
                        </Col>
                        <Col md={3}>
                            <DatePicker
                                label={t("common.dateTo")}
                                name={nameof<typeof teacherLanguage>("dateTo")}
                                value={teacherLanguage.dateTo}
                                onChange={(e) => handleDateToChange(e, index)}
                                isClearable={true}
                                plaintext={readonly}                                  
                                minYear={new Date().getFullYear() - 25}
                                maxYear={new Date().getFullYear() + 25}
                            />
                        </Col>
                        {!readonly &&
                        <Col md={3}>
                            <UploadButton                    
                                label={t("teacherLanguage.certificationDocument")}
                                name={nameof<typeof teacherLanguage>("certificationDocument")}
                                value={teacherLanguage.certificationDocument}                    
                                documentType={DocumentType.TeacherLanguageCertification}
                                toolTip={t("document.imagesAndPdfTooltip")}
                                fileFilter={t("document.imagesAndPdfFilter")}
                                onFileAdded={(file, e) => handleCertificationDocumentAdded(e, index, file)}
                                onFileRemoved={(e) => handleCertificationDocumentRemoved(e, index)}
                                errorText={getErrorForPath(`${name}${index}.certificationDocument`)}
                            />
                        </Col>
                        }
                        {readonly &&
                        <Col md={3} className={styles.hideUploadButton}>
                            {teacherLanguage.certificationDocument &&
                            <UploadButton
                                label={t("teacherLanguage.certificationDocument")}
                                name={nameof<typeof teacherLanguage>("certificationDocument")}
                                value={teacherLanguage.certificationDocument}
                                disabled={true}
                                documentType={DocumentType.TeacherLanguageCertification}
                            /> ||
                            <StaticField
                                label={t("teacherLanguage.certificationDocument")}                           
                                value={teacherLanguage.certificationDocument?.fileName}
                                className={styles.changedFieldModule}
                            />
                            }
                        </Col>
                        }
                    </Row>
                    {(!readonly) &&
                    <Row>
                        <Col md={12} className="text-right">
                            <Button
                                className={`p-0 ${index > 0 ? 'visible' : 'invisible'}`}
                                boxless
                                disabled={readonly}
                                onClick={(e) => handleRemove(e, index)}
                            >
                                {t("teacherLanguage.remove")}
                            </Button>
                        </Col>
                    </Row>                        
                    }
                </div>
            ))}
            {(!readonly) &&
            <Row className={"text-right mt-3"}>
                <Col md={12}>                    
                    <ButtonWithValidation
                        outline
                        icon={<AddIcon/>}
                        onClick={(e) => handleAdd(e)}                        
                        errorText={getErrorForPath(`${name}`)}
                    >
                        {t("teacherLanguage.add")}
                    </ButtonWithValidation>
                </Col>
            </Row>
            }
        </>
    );
};